import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Hexagon, ArrowLeft, ArrowRight, Loader2, Salad, Flame, ShoppingCart,
  Lightbulb, RefreshCw, ChevronRight, UtensilsCrossed,
} from "lucide-react";
import Orb from "@/components/Orb";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const QUESTIONS = [
  { key: "goal", q: "What's your main nutrition goal?",
    options: ["Lose fat", "Build muscle", "Maintain & feel good", "Athletic performance", "Improve overall health"] },
  { key: "style", q: "Which eating style suits you?",
    options: ["Omnivore (everything)", "Vegetarian", "Vegan", "Pescatarian", "Keto / low-carb", "Paleo", "Mediterranean", "Halal"] },
  { key: "avoid", q: "Anything to avoid or you're allergic to? (choose any)", type: "multi",
    options: ["Dairy", "Gluten", "Nuts", "Shellfish", "Eggs", "Soy", "Pork", "None"] },
  { key: "taste", q: "What matters MOST to you in your food?",
    options: ["Comfort & great taste", "High energy all day", "Simplicity & convenience", "Variety & new flavors", "Strict results, whatever it takes"] },
  { key: "meals_per_day", q: "How many meals per day do you prefer?",
    options: ["2", "3", "4", "5+"] },
  { key: "cooking", q: "How much do you want to cook?",
    options: ["Minimal (under 15 min)", "Moderate", "I love cooking"] },
  { key: "budget", q: "What's your grocery budget?",
    options: ["Tight", "Moderate", "Flexible"] },
];

export default function Nutrition() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [plan, setPlan] = useState(null);
  const [checking, setChecking] = useState(true);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ avoid: [], meals_per_day: "3" });
  const [generating, setGenerating] = useState(false);
  const [quizMode, setQuizMode] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/nutrition/plan");
      if (data.plan) setPlan(data.plan); else setQuizMode(true);
    } catch { setQuizMode(true); } finally { setChecking(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const current = QUESTIONS[step];

  const answer = (val) => {
    if (current.type === "multi") {
      const list = answers.avoid || [];
      const next = list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
      setAnswers({ ...answers, avoid: next });
      return;
    }
    setAnswers({ ...answers, [current.key]: val });
    if (step < QUESTIONS.length - 1) setStep(step + 1); else generate({ ...answers, [current.key]: val });
  };

  const generate = async (final) => {
    setGenerating(true); setErr("");
    try {
      const { data } = await api.post("/nutrition/generate", final || answers);
      setPlan(data.plan); setQuizMode(false);
    } catch { setErr("The System couldn't design your diet just now. Please try again."); }
    finally { setGenerating(false); }
  };

  const retake = () => { setPlan(null); setStep(0); setAnswers({ avoid: [], meals_per_day: "3" }); setQuizMode(true); };

  if (checking) return <div className="h-screen grid-bg flex items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={30} /></div>;

  if (generating) {
    return (
      <div className="h-screen grid-bg flex flex-col items-center justify-center text-center px-6">
        <Orb state="thinking" size={180} />
        <p className="mono-label mt-10">DESIGNING NUTRITION</p>
        <h2 className="font-display text-3xl font-bold mt-3">Architecting your diet…</h2>
        <Loader2 className="animate-spin text-cyan-400 mt-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-bg">
      <div className="fixed inset-0 glow-radial pointer-events-none" />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-blue-900/30 backdrop-blur">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-cyan-300" data-testid="back-btn"><ArrowLeft size={18} /></button>
          <div className="flex items-center gap-2">
            <Salad className="text-cyan-400" size={18} />
            <div>
              <p className="font-display font-semibold leading-tight">Nutrition Architect</p>
              <p className="mono-label">ANY DIET, BUILT FOR YOU</p>
            </div>
          </div>
        </div>
        {plan && !quizMode && (
          <button data-testid="retake-btn" onClick={retake} className="mono-label flex items-center gap-1.5 border border-blue-900/40 px-3 py-1.5 rounded-full hover:bg-blue-500/10 transition-all">
            <RefreshCw size={13} /> Redo Quiz
          </button>
        )}
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {quizMode ? (
          <div className="max-w-2xl mx-auto" data-testid="nutrition-quiz">
            {err && <p data-testid="gen-error" className="mb-4 text-red-400 text-sm border border-red-500/30 bg-red-500/5 rounded-lg px-4 py-3">{err}</p>}
            <div className="flex items-center gap-3 mb-8">
              <Hexagon className="text-cyan-400" size={16} />
              <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
              </div>
              <span className="mono-label">{step + 1}/{QUESTIONS.length}</span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <p className="mono-label">NUTRITION PROFILE</p>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 mb-8">{current.q}</h2>
                <div className="space-y-3">
                  {current.options.map((opt) => {
                    const active = current.type === "multi" ? (answers.avoid || []).includes(opt) : answers[current.key] === opt;
                    return (
                      <button key={opt} data-testid={`option-${opt.slice(0, 18)}`} onClick={() => answer(opt)}
                        className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between ${active ? "border-cyan-400/60 bg-cyan-400/10 text-slate-50" : "border-blue-900/40 text-slate-300 hover:bg-white/5 hover:border-blue-500/40"}`}>
                        <span>{opt}</span>{active && <ChevronRight size={16} className="text-cyan-400" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-between mt-10">
              <button data-testid="quiz-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                className="flex items-center gap-2 text-slate-400 disabled:opacity-30 hover:text-cyan-300 transition-colors"><ArrowLeft size={16} /> Back</button>
              {current.type === "multi" && (
                <button data-testid="quiz-next" onClick={() => { if (step < QUESTIONS.length - 1) setStep(step + 1); else generate(); }}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-full font-medium transition-all hover:shadow-[0_0_22px_rgba(59,130,246,0.5)]">
                  {step === QUESTIONS.length - 1 ? "Build my diet" : "Next"} <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        ) : plan ? (
          <PlanView plan={plan} />
        ) : null}
      </div>
    </div>
  );
}

function PlanView({ plan }) {
  return (
    <div className="space-y-8" data-testid="nutrition-plan">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 sm:p-8">
        <p className="mono-label">YOUR PROTOCOL</p>
        <h1 className="font-display text-3xl font-extrabold text-cyan-300 mt-1" data-testid="diet-type">{plan.diet_type}</h1>
        <p className="text-slate-300 mt-3 max-w-3xl leading-relaxed">{plan.summary}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="DAILY TARGET" value={plan.calorie_target} />
          <Stat label="PROTEIN" value={plan.macros?.protein} />
          <Stat label="CARBS" value={plan.macros?.carbs} />
          <Stat label="FAT" value={plan.macros?.fat} />
        </div>
      </motion.div>

      <section data-testid="meals-section">
        <Title icon={UtensilsCrossed} label="MEAL PLAN" />
        <div className="grid md:grid-cols-2 gap-4">
          {(plan.meals || []).map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="glass rounded-xl p-5" data-testid={`meal-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-display font-bold text-lg">{m.name}</p>
                <span className="mono-label">{m.calories}</span>
              </div>
              <ul className="space-y-1.5">
                {(m.items || []).map((it, j) => (
                  <li key={j} className="text-sm text-slate-300 flex gap-2"><span className="text-cyan-400">•</span>{it}</li>
                ))}
              </ul>
              {m.note && <p className="text-xs text-slate-500 mt-3">{m.note}</p>}
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="grocery-section">
        <Title icon={ShoppingCart} label="GROCERY LIST" />
        <div className="glass rounded-xl p-5 flex flex-wrap gap-2">
          {(plan.grocery_list || []).map((g, i) => (
            <span key={i} className="text-sm text-slate-200 border border-blue-900/40 rounded-full px-3 py-1.5" data-testid={`grocery-${i}`}>{g}</span>
          ))}
        </div>
      </section>

      <section className="pb-10" data-testid="tips-section">
        <Title icon={Lightbulb} label="ARCHON'S DIRECTIVES" />
        <div className="glass rounded-xl p-5 space-y-3">
          {(plan.tips || []).map((t, i) => (
            <div key={i} className="flex gap-3 text-slate-300" data-testid={`tip-${i}`}>
              <span className="mono-label text-cyan-400 shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm leading-relaxed">{t}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="border border-blue-900/30 rounded-xl p-4 text-center">
    <p className="mono-label">{label}</p>
    <p className="font-display text-xl font-bold text-slate-50 mt-1">{value || "—"}</p>
  </div>
);
const Title = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3 mb-4"><Icon className="text-cyan-400" size={18} /><h2 className="font-display text-lg font-semibold tracking-wide">{label}</h2></div>
);
